import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 ore di inattività
const CHECK_INTERVAL_MS = 30 * 1000;      // controllo ogni 30s
const REFRESH_AFTER_MS = 15 * 60 * 1000;  // rinnova il token max ogni 15 min (se attivo)
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());

  const doLogout = async ({ inactivity = false } = {}) => {
    const hadToken = !!localStorage.getItem("token");
    localStorage.removeItem("token");
    localStorage.removeItem("lastActivity");
    setUser(null);
    try { if (hadToken) await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    if (inactivity) toast.error("Sessione scaduta per inattività");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    const last = parseInt(localStorage.getItem("lastActivity") || "0", 10);
    if (last && Date.now() - last >= IDLE_LIMIT_MS) {
      localStorage.removeItem("token");
      localStorage.removeItem("lastActivity");
      setLoading(false);
      toast.error("Sessione scaduta per inattività");
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        lastActivityRef.current = Date.now();
        localStorage.setItem("lastActivity", String(Date.now()));
      })
      .catch(() => { localStorage.removeItem("token"); localStorage.removeItem("lastActivity"); })
      .finally(() => setLoading(false));
  }, []);

  // Rilevamento attività + timer inattività (solo quando loggato)
  useEffect(() => {
    if (!user) return;
    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem("lastActivity", String(now));
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const tick = setInterval(async () => {
      const now = Date.now();
      const idle = now - lastActivityRef.current;
      if (idle >= IDLE_LIMIT_MS) {
        await doLogout({ inactivity: true });
        return;
      }
      // Sliding session: rinnova il token mentre l'utente è attivo
      if (now - lastRefreshRef.current >= REFRESH_AFTER_MS) {
        try {
          const { data } = await api.post("/auth/refresh");
          if (data?.token) localStorage.setItem("token", data.token);
          lastRefreshRef.current = now;
        } catch (e) { /* ignore */ }
      }
    }, CHECK_INTERVAL_MS);

    // Logout forzato su 401 dal server
    const onExpired = () => { doLogout({ inactivity: true }); };
    window.addEventListener("auth-expired", onExpired);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(tick);
      window.removeEventListener("auth-expired", onExpired);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    localStorage.setItem("lastActivity", String(Date.now()));
    lastActivityRef.current = Date.now();
    lastRefreshRef.current = Date.now();
    setUser(data.user);
    return data.user;
  };

  const logout = async () => { await doLogout(); };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
